import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const NAV = [
  { to: "/technology", label: "Technology" },
  { to: "/companies", label: "Companies" },
  { to: "/labs", label: "ImJove Labs" },
  { to: "/founder", label: "Founder" },
  { to: "/mission", label: "Mission" },
  { to: "/newsroom", label: "Newsroom" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { content } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 md:px-12">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={content.logo_url} alt="ImJove Tech" className="h-9 w-9 object-contain" />
          <span className="font-display text-sm font-semibold uppercase tracking-brand text-white">
            ImJove<span className="text-gold"> Tech</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 xl:flex">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-body text-[13px] tracking-wide transition-colors hover:text-white ${
                loc.pathname === n.to ? "text-gold-light" : "text-white/60"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/universe"
            data-testid="nav-cta-ecosystem"
            className="btn-ghost hidden items-center gap-2 rounded-full px-5 py-2.5 font-mono2 text-[11px] uppercase tracking-widest md:inline-flex"
          >
            Explore the Ecosystem <ArrowRight size={13} />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="rounded-full border border-white/15 p-2.5 text-white xl:hidden"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="glass overflow-hidden border-t border-white/10 xl:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <Link
                    to={n.to}
                    className="block py-3 font-display text-2xl font-light text-white/80 hover:text-gold-light"
                  >
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <Link
                to="/universe"
                className="btn-gold mt-4 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-mono2 text-[11px] uppercase tracking-widest"
              >
                Explore the Ecosystem <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
